import React from "react";
import { Link } from "react-router-dom";
import Select from "react-select";

export const statusLabel = (status) =>
  String(status || "")
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

// One tone per project state, shared by the form header and the list.
export const STATUS_TONE = {
  active: "coa-pill-dr",
  completed: "coa-pill-ok",
  on_hold: "coa-pill-cr",
  inactive: "coa-pill-muted",
};

export const ProjectStatusPill = ({ status }) => (
  <span className={`coa-pill ${STATUS_TONE[status] || "coa-pill-muted"}`}>
    {statusLabel(status) || "—"}
  </span>
);

// Fields shared by the create and edit screens; state, option loading and
// saving stay with the page.
const ProjectForm = ({
  inputs,
  options,
  selectedCustomer,
  selectedCostCenter,
  onChange,
  onCustomerChange,
  onCostCenterChange,
  onSubmit,
  saving,
  submitLabel,
  codeHint,
}) => (
  <>
    <div className="coa-panel">
      <div className="coa-panel-head">
        <span>
          <i className="fa fa-briefcase me-2"></i>Project Identity
        </span>
        <ProjectStatusPill status={inputs.status} />
      </div>
      <div className="coa-panel-body">
        <div className="row">
          <div className="col-lg-3">
            <label>
              Code <i className="fa fa-star required"></i>
            </label>
            <input
              type="text"
              className="form-control coa-mono"
              name="code"
              value={inputs.code}
              onChange={onChange}
            />
            {codeHint && <small className="coa-hint">{codeHint}</small>}
          </div>
          <div className="col-lg-6 mg-t-10 mg-lg-t-0">
            <label>
              Name <i className="fa fa-star required"></i>
            </label>
            <input
              type="text"
              className="form-control"
              name="name"
              value={inputs.name}
              onChange={onChange}
              placeholder="e.g. Warehouse Fit-out 2026"
            />
          </div>
          <div className="col-lg-3 mg-t-10 mg-lg-t-0">
            <label>Status</label>
            <select
              className="form-control"
              name="status"
              value={inputs.status}
              onChange={onChange}
            >
              {options.statuses?.map((status) => (
                <option key={status} value={status}>
                  {statusLabel(status)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>

    <div className="coa-panel">
      <div className="coa-panel-head">
        <span>
          <i className="fa fa-handshake-o me-2"></i>Customer &amp; Cost Center
        </span>
        <span className="coa-head-note">Both optional</span>
      </div>
      <div className="coa-panel-body">
        <div className="row">
          <div className="col-lg-6">
            <label>Customer</label>
            <Select
              className="basic-single"
              classNamePrefix="select"
              placeholder="Internal (no customer)"
              name="acc_customer_id"
              options={options.customers}
              value={selectedCustomer}
              onChange={onCustomerChange}
              maxMenuHeight={320}
              isClearable
              isSearchable
            />
            <small className="coa-hint">
              One customer can own many projects.
            </small>
          </div>
          <div className="col-lg-6 mg-t-10 mg-lg-t-0">
            <label>Cost Center</label>
            <Select
              className="basic-single"
              classNamePrefix="select"
              placeholder="Select Cost Center"
              name="acc_cost_center_id"
              options={options.cost_centers}
              value={selectedCostCenter}
              onChange={onCostCenterChange}
              maxMenuHeight={320}
              isClearable
              isSearchable
            />
            <small className="coa-hint">
              {selectedCustomer
                ? "This customer's cost centers, plus internal ones."
                : "Pick a customer first to narrow this list."}
            </small>
          </div>
        </div>
      </div>
    </div>

    <div className="coa-panel">
      <div className="coa-panel-head">
        <span>
          <i className="fa fa-calendar-o me-2"></i>Schedule &amp; Budget
        </span>
      </div>
      <div className="coa-panel-body">
        <div className="row">
          <div className="col-lg-3">
            <label>Start Date</label>
            <input
              type="date"
              className="form-control"
              name="start_date"
              value={inputs.start_date || ""}
              onChange={onChange}
              max={inputs.end_date || undefined}
            />
          </div>
          <div className="col-lg-3 mg-t-10 mg-lg-t-0">
            <label>End Date</label>
            <input
              type="date"
              className="form-control"
              name="end_date"
              value={inputs.end_date || ""}
              onChange={onChange}
              min={inputs.start_date || undefined}
            />
          </div>
          <div className="col-lg-3 mg-t-10 mg-lg-t-0">
            <label>Budget Amount</label>
            <input
              type="number"
              step="0.01"
              className="form-control coa-num"
              name="budget_amount"
              value={inputs.budget_amount}
              onChange={onChange}
              placeholder="0.00"
            />
          </div>
        </div>
        <div className="row mg-t-18">
          <div className="col-lg">
            <label>Remarks</label>
            <textarea
              className="form-control"
              name="description"
              rows="2"
              value={inputs.description || ""}
              onChange={onChange}
              placeholder="Scope or notes for this project"
            />
          </div>
        </div>
      </div>
    </div>

    <div className="d-flex justify-content-between align-items-center">
      <Link to="/acc-project" className="btn btn-outline-secondary">
        Cancel
      </Link>
      <button
        type="button"
        className="btn btn-primary"
        onClick={onSubmit}
        disabled={saving}
      >
        <i
          className={`fa ${saving ? "fa-spinner fa-spin" : "fa-send"} mg-r-10`}
        ></i>
        {saving ? "Saving..." : submitLabel}
      </button>
    </div>
  </>
);

export default ProjectForm;
